import { VAR_REGIONS } from '../../types/var';
import type { RegionStats } from './statsTypes';

// Stats data is keyed by the RAW region string stored in the DB
// (`data->'objective'->'region'->>'name'`), which is NOT canonicalized server-side.
// The UI sub-tabs use canonical display labels. `Deh Cho` (label) vs `Dehcho` (DB) is
// the #637 mismatch. VAR_REGIONS already declares every label and its name variants
// (`match`), so we derive the alias map from that single source of truth rather than
// maintaining a second copy here.
const REGION_ALIASES: Record<string, string[]> = Object.fromEntries(
  VAR_REGIONS.map(r => [r.label, r.match]),
);

/**
 * Look up a region's stats by its canonical display label, falling back to the
 * raw DB name variants when the data is keyed under an un-canonicalized string.
 * Exact label matches always win over aliases.
 */
export function resolveRegionStats(
  regions: Record<string, RegionStats>,
  displayName: string,
): RegionStats | null {
  if (regions[displayName]) return regions[displayName];
  for (const alias of REGION_ALIASES[displayName] ?? []) {
    if (regions[alias]) return regions[alias];
  }
  return null;
}

/**
 * Map a raw region key (as it appears in stats data) back to its canonical display
 * label, so chart axes read consistently with the sub-tab labels. Returns the input
 * unchanged when no canonical label is known.
 */
export function canonicalRegionLabel(rawName: string): string {
  for (const [label, aliases] of Object.entries(REGION_ALIASES)) {
    if (label === rawName || aliases.includes(rawName)) return label;
  }
  return rawName;
}
